import React from 'react';
import { Box, Stack, Chip, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import { ReactComponent as IconPaidDate } from '@assets/svg/paid-date.svg';
import { TextView } from '@atoms';
import { callGetMarkupValues } from '@api';
import { formatBillingDate } from '@particles';

interface LocationDetailsProps {
  location?: CoreLocation;
  website?: string;
  offPatchValuationAddress?: string;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({
  location
}: LocationDetailsProps) => {
  // Markup catalogue is small and global; cache it via React Query so every
  // CoreDetails open doesn't re-fetch the whole list. It changes only when
  // a superadmin edits markup-margin, so 30-minute staleness is fine.
  const markupQuery = useQuery({
    queryKey: ['markup-values/catalogue'],
    queryFn: () => callGetMarkupValues(),
    enabled: !!location?.priceModel,
    staleTime: 30 * 60 * 1000
  });

  const aiPlanLabel =
    markupQuery.data?.code === 200 && markupQuery.data.data?.data && location?.priceModel
      ? markupQuery.data.data.data.find((m) => m.type === location.priceModel)?.title ?? ''
      : '';

  const subscriptionRange =
    location?.subscriptionDate && location?.nextSubscriptionDate
      ? `${formatBillingDate(location.subscriptionDate, 'dd LLL yyyy')} - ${formatBillingDate(
          location.nextSubscriptionDate,
          'dd LLL yyyy'
        )}`
      : '-';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        background: `linear-gradient(135deg, ${alpha('#E8EDDF', 1)} 0%, ${alpha('#FFFFFF', 1)} 70%)`,
        p: { xs: 2, sm: 3 },
        boxShadow:
          '0px 7px 140px rgba(141, 152, 121, 0.1), 0px 2px 32px rgba(141, 152, 121, 0.05), 0px 1px 10px rgba(141, 152, 121, 0.04)',
        borderRadius: 2,
        border: '1px solid rgba(36, 36, 35, 0.08)'
      }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextView $fontSize={22} sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              {location?.companyName ?? '-'}
            </TextView>
            <Chip
              size="small"
              label={`${location?.status?.toUpperCase() ?? '-'}`}
              variant="filled"
              sx={{
                borderRadius: 999,
                fontWeight: 800,
                letterSpacing: 0.4,
                backgroundColor:
                  location?.status === 'live'
                    ? alpha('#2E7D32', 0.14)
                    : location?.status === 'inactive'
                    ? alpha('#ED6C02', 0.14)
                    : alpha('#242423', 0.08),
                color:
                  location?.status === 'live'
                    ? '#1B5E20'
                    : location?.status === 'inactive'
                    ? '#ED6C02'
                    : '#242423'
              }}
            />
            {location?.priceModel && (
              <Chip
                size="small"
                label={aiPlanLabel || location.priceModel}
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.3,
                  borderColor: alpha('#1565C0', 0.4),
                  color: '#1565C0'
                }}
              />
            )}
            {location?.businessType && (
              <Chip
                size="small"
                label={location.businessType.toUpperCase()}
                variant="filled"
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.3,
                  backgroundColor: alpha('#6A1B9A', 0.14),
                  color: '#6A1B9A'
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TextView $fontWeight={600} $fontSize={13}>
                {`ID: ${location?.locationId ?? '-'}`}
              </TextView>
              {location?.locationId && (
                <Tooltip title="Copy location ID">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(location.locationId);
                      } catch {
                        // no-op
                      }
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ContentCopyOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {/* Subscription (moved here for better hierarchy) */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 1.25, flexWrap: 'wrap' }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha('#8D9879', 0.18),
                flexShrink: 0
              }}>
              <IconPaidDate style={{ width: 20, height: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <TextView $fontWeight={800} $fontSize={12} sx={{ opacity: 0.8 }}>
                Subscription
              </TextView>
              <TextView $fontWeight={600} $fontSize={13} sx={{ mt: 0.25 }}>
                {subscriptionRange}
              </TextView>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default LocationDetails;
